import { motion } from 'framer-motion';
import {
  Smartphone,
  Shield,
  Globe,
  Coins,
  Cpu,
  Download,
  Signal,
  Zap,
  Brain,
  Lock,
  Mic,
  MessageSquare,
  Sparkles,
  ChevronRight,
  Package,
  ExternalLink,
  Clock,
  Truck,
  Monitor,
  Eye,
  Menu,
  LayoutDashboard,
  Moon,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { usePhoneInventory } from '../lib/usePhoneInventory';
import type { PhoneInventory } from '../lib/usePhoneInventory';
import { useCheckout } from '../lib/useCheckout';

const SPECS = [
  { label: 'Operating System', value: 'SELF OS (on Android 9+)' },
  { label: 'Processor', value: 'MTK6765 Octa-Core 2.35GHz + 1.7GHz' },
  { label: 'Memory', value: '4GB DDR4 RAM' },
  { label: 'Storage', value: '64GB standard (expandable)' },
  { label: 'Screen', value: '6.3" HD+ Touchscreen' },
  { label: 'Battery', value: '5000mAh (48+ hour life)' },
  { label: 'Connectivity', value: 'Global Satellite, Wi-Fi 6, Bluetooth 5.2, Dual SIM' },
  { label: 'Weight & Durability', value: '0.32 kg, IP68 dust/water resistant' },
  { label: 'Interface', value: 'USB-C 2.0 OTG Port' },
];

const FEATURES = [
  {
    icon: Globe,
    title: 'Free Global Signal',
    body: 'Communicate worldwide without carrier plans. Powered by satellite providers — full independence, anywhere on Earth.',
  },
  {
    icon: Cpu,
    title: 'Resonatia Mesh Node',
    body: 'Download models from Resonatia, sync to the network, and earn Essence when your node helps process API requests.',
  },
  {
    icon: Brain,
    title: 'Role Model AI',
    body: 'Native AI App Store. Install AI apps, agents, and tools. Your Concierge avatar can access and operate apps for you by voice or signal.',
  },
  {
    icon: Mic,
    title: 'Your Concierge Avatar',
    body: 'Create your own avatar trained in your voice and style. Speak through it, command with it, connect through it.',
  },
  {
    icon: MessageSquare,
    title: 'Agent Communication',
    body: 'Instant agent-to-agent and user-to-user walkie-talkie communication. Encrypted, carrier-free, built for real-time execution.',
  },
  {
    icon: Shield,
    title: 'Aura Shield',
    body: 'Always-on data encryption and protection. Device-level security from day one. Full data monetization control unlocks in Phase 2.',
  },
  {
    icon: Coins,
    title: 'Private Earnings',
    body: 'Earn Essence when your Resonatia node processes API requests, plus rewards from Concierge task signals and optional signal monetization.',
  },
  {
    icon: Signal,
    title: 'Signal Governance',
    body: 'Participate in the Signal Economy as a node operator, earning rewards and shaping the future of decentralized communication.',
  },
];

const WHATS_IN_BOX = [
  { title: 'SELF Phone', detail: '1× SELF Phone (First Wave Edition)' },
  { title: 'Charger', detail: '1× USB-C Fast Charger' },
  { title: 'Documentation', detail: 'Technical Specs Manual & Setup Guide' },
  { title: 'Onboarding Kit', detail: '1× Signal Onboarding Kit' },
  { title: 'Software', detail: 'Pre-Activated Aura Shield & SELF OS' },
];

const SECURITY_FEATURES = [
  {
    icon: Shield,
    title: 'Zero-Knowledge Encryption',
    body: 'Zero-knowledge encryption at the hardware and live signal layers. No external entity can see or alter tasks or transmissions.',
  },
  {
    icon: Lock,
    title: 'Aura Shield Protection',
    body: 'Always-on personal data protection. No monthly fees, no surveillance. Full user-controlled data sharing and monetization.',
  },
  {
    icon: Signal,
    title: 'Private Signal Routing',
    body: 'Secure, decentralized mesh routing with no centralized control points or vulnerabilities.',
  },
];

const NODE_STEPS = [
  {
    icon: Download,
    title: 'Download Models',
    body: 'Pull edge-ready models from Resonatia onto your SELF Phone.',
  },
  {
    icon: Cpu,
    title: 'Sync to Mesh',
    body: 'Models register on the Resonatia network so your device becomes an active inference node.',
  },
  {
    icon: Zap,
    title: 'Process API Calls',
    body: 'When someone hits the Resonatia API, requests can be served by nodes like yours on the mesh.',
  },
  {
    icon: Coins,
    title: 'Earn Essence',
    body: 'Your node earns Essence rewards for every API request it helps process.',
  },
];

function GlowBackground() {
  return (
    <div
      className="pointer-events-none fixed inset-0 opacity-20"
      style={{
        background:
          'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(56, 189, 248, 0.15) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 10% 80%, rgba(34, 211, 238, 0.08) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 90% 70%, rgba(56, 189, 248, 0.08) 0%, transparent 45%)',
      }}
    />
  );
}

function SectionBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/70">
      {children}
    </span>
  );
}

function FadeInUp({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
    >
      {children}
    </motion.div>
  );
}

function GlowButton({
  children,
  href,
  onClick,
  disabled = false,
  primary = false,
  hero = false,
}: {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
  hero?: boolean;
}) {
  const Tag = onClick && !href ? 'button' : 'a';
  const extraProps = Tag === 'button'
    ? { onClick, disabled, type: 'button' as const }
    : { href, target: href?.startsWith('http') ? '_blank' : undefined, rel: href?.startsWith('http') ? 'noopener noreferrer' : undefined };

  if (hero) {
    return (
      <Tag
        {...extraProps}
        className={`group relative inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-white transition-all duration-300 ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-sky-500 via-cyan-400 to-sky-400 transition-all duration-500 group-hover:from-sky-400 group-hover:via-cyan-300 group-hover:to-sky-300" />
        <span className="absolute inset-[1px] rounded-[calc(0.75rem-1px)] bg-black transition-all duration-300 group-hover:bg-black/80" />
        <span className="absolute -inset-1 rounded-xl bg-gradient-to-r from-sky-400/50 via-cyan-400/30 to-sky-400/50 opacity-0 blur-lg transition-all duration-500 group-hover:opacity-100" />
        <span className="relative z-10 flex items-center gap-2">{children}</span>
      </Tag>
    );
  }
  if (primary) {
    return (
      <Tag
        {...extraProps}
        className={`btn-glow group inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-black transition-all duration-300 hover:shadow-[0_0_24px_rgba(56,189,248,0.5)] ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span className="relative z-10">{children}</span>
      </Tag>
    );
  }
  return (
    <Tag
      {...extraProps}
      className={`group inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:border-sky-400/50 hover:bg-white/10 hover:shadow-[0_0_20px_rgba(56,189,248,0.25)] ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {children}
    </Tag>
  );
}

function GlowCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`card-glow rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-all duration-300 hover:border-sky-400/20 hover:shadow-[0_0_16px_rgba(56,189,248,0.08)] ${className}`}
    >
      {children}
    </div>
  );
}

function BatchSummary({ inventory, loading }: { inventory: PhoneInventory; loading: boolean }) {
  const openBatch = inventory.batches.find((b) => b.status === 'open');
  const claimedPct =
    inventory.total > 0 ? Math.min(100, (inventory.sold / inventory.total) * 100) : 0;

  return (
    <div className="rounded-2xl border border-sky-400/20 bg-gradient-to-br from-sky-500/5 via-white/[0.02] to-transparent p-6 transition-all duration-300 hover:border-sky-400/30 hover:shadow-[0_0_20px_rgba(56,189,248,0.06)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-400/30 bg-sky-500/10">
            <Clock className="h-5 w-5 text-sky-400" />
          </div>
          <div>
            <p className="text-sm text-white/70">
              {inventory.soldOut
                ? 'All 50 phones have been claimed'
                : `${inventory.remaining} of ${inventory.total} phones available`}
            </p>
            {!inventory.soldOut && openBatch && (
              <p className="text-xs text-white/40 mt-0.5">
                Batch {openBatch.number} ships {openBatch.shipLabel}
                {inventory.nextOrderBatch && inventory.nextOrderBatch !== openBatch.number && (
                  <> · new orders join Batch {inventory.nextOrderBatch}</>
                )}
              </p>
            )}
          </div>
        </div>
        {!inventory.soldOut && (
          <span className="rounded-full border border-sky-400/20 bg-sky-500/10 px-3 py-1 text-xs font-medium text-sky-400">
            {inventory.sold} claimed
          </span>
        )}
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-sky-400 to-cyan-400 transition-all duration-700 ease-out"
          style={{ width: `${claimedPct}%` }}
        />
      </div>
    </div>
  );
}

export default function HomePage() {
  const { inventory, loading } = usePhoneInventory();
  const { openCheckout, loading: checkoutLoading } = useCheckout();
  const openBatch = inventory.batches.find((b) => b.status === 'open');
  const claimedPct =
    inventory.total > 0 ? Math.min(100, (inventory.sold / inventory.total) * 100) : 0;

  return (
    <>
      {/* 1. Hero */}
      <section className="relative isolate min-h-[90svh] overflow-hidden">
        <GlowBackground />

        <div className="relative z-10 mx-auto flex min-h-[90svh] max-w-6xl items-center px-4 pb-12 pt-24 sm:px-6 lg:px-8">
          <div className="grid w-full items-center gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <SectionBadge>
                <Smartphone className="h-3.5 w-3.5" />
                Resonatia mesh node
              </SectionBadge>

              <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl">
                Your Voice.
                <br />
                Your AI.
                <br />
                <span className="bg-gradient-to-r from-sky-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent drop-shadow-[0_0_12px_rgba(56,189,248,0.3)]">
                  Your Signal.
                </span>
              </h1>

              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/60 sm:text-xl">
                SELF Phone is the node for Resonatia — download models, sync them to the mesh, and
                earn Essence when your device helps process API requests. Already on Android? Install
                SELF OS and run as a node too.
              </p>

              <div className="mt-8">
                <BatchSummary inventory={inventory} loading={loading} />
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row">
                <GlowButton onClick={openCheckout} disabled={checkoutLoading} hero>
                  {checkoutLoading ? 'Opening checkout...' : 'Buy Now $999'}
                  <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </GlowButton>
                <GlowButton href="https://selflabs.xyz/download">
                  <Download className="h-5 w-5" />
                  Download SELF OS beta
                </GlowButton>
              </div>
              <p className="mt-3 text-sm text-white/40">
                Android 9+ · sideload · public beta
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex items-center justify-center"
            >
              <div className="relative animate-float-glow">
                <div className="absolute -inset-8 rounded-full bg-sky-400/5 blur-3xl" />
                <motion.img
                  src="/assets/images/Untitled design (1).svg"
                  alt="SELF Phone Preview"
                  className="relative w-full max-w-[400px] rounded-3xl border border-white/10 shadow-[0_0_30px_rgba(56,189,248,0.06)]"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
              <p className="absolute -bottom-8 text-center text-xs italic text-white/40">
                *Actual device appearance may vary from images shown
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. What You Get */}
      <section id="what-you-get" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Sparkles className="h-3.5 w-3.5" />
                Everything included
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                What You Get
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Every SELF Phone comes with the full SELF Labs ecosystem built in — no subscriptions,
                no hidden fees.
              </p>
            </div>
          </FadeInUp>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((feature, i) => (
              <FadeInUp key={feature.title} delay={i * 0.04}>
                <GlowCard className="h-full">
                  <feature.icon className="h-8 w-8 text-sky-400" />
                  <h3 className="mt-4 font-semibold">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{feature.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SELF OS */}
      <section id="selfos" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">

          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeInUp>
              <div>
                <SectionBadge>
                  <Download className="h-3.5 w-3.5" />
                  Public beta 0.2.3-beta
                </SectionBadge>
                <h2 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                  SELF OS — your AI, your phone
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-white/60">
                  SELF OS turns your existing Android phone into a full Role Model AI device. Your avatar
                  lives on the home screen, the side button summons it instantly, and your phone earns
                  Essence while you sleep.
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <GlowButton href="https://selflabs.xyz/download" hero>
                    <Download className="h-5 w-5" />
                    Download SELF OS beta
                  </GlowButton>
                  <GlowButton href="https://concierge.rolemodel.ai">
                    Create your avatar
                    <ArrowRight className="h-5 w-5" />
                  </GlowButton>
                </div>
                <p className="mt-4 text-sm text-white/40">Android 9+ · sideload · free · public beta</p>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.15}>
              <div className="relative flex items-center justify-center">
                <div className="relative animate-float-glow">
                  <div className="absolute -inset-4 rounded-full bg-sky-400/5 blur-3xl" />
                  <img
                    src="/assets/images/ChatGPT Image Aug 6, 2026, 04_49_30 PM.png"
                    alt="SELF OS on Android phone"
                    className="relative w-full max-w-[500px] rounded-3xl border border-white/10 shadow-[0_0_30px_rgba(56,189,248,0.06)]"
                  />
                </div>
              </div>
            </FadeInUp>
          </div>

          <div className="mx-auto mt-20 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Brain,
                title: 'Your avatar lives on the phone',
                body: 'Sign in with your Role Model AI account and pick your avatar. It opens full-screen like a voice assistant. No avatar yet? The app walks you through the same creation flow as Concierge — look, custom voice, and knowledge. Publish your Living Bio and Talk is live.',
              },
              {
                icon: Monitor,
                title: 'Side-button wake & phone control',
                body: 'The SELF OS Wake accessibility service turns your side button into an avatar summon. Your assistant can operate the phone: Back, Recents, Notifications, open apps by name, Home, Settings — real phone control, not just voice.',
              },
              {
                icon: LayoutDashboard,
                title: 'Full Concierge built in',
                body: 'The same Concierge you know runs as tiles on your home dashboard: Avatar (change, create, edit, Living Bio, Embed, OBS Stream, Meetings — send your avatar into Zoom/Google Meet/Teams — Chat History), Life OS (timeline, goals, memory, daily brief, weekly review), Messaging (reminders, inbox), Home Intel, All Tools, and more.',
              },
              {
                icon: Coins,
                title: 'Wallet — your credits & billing',
                body: 'The Wallet tab shows your Concierge creator credits, Talk credit balance, and billing history. Top up, track usage, and manage your subscription — all from your phone.',
              },
              {
                icon: Sparkles,
                title: 'Rewards — Essence earnings',
                body: 'The Rewards tab tracks every Essence you earn from idle compute. See your balance, conversion history, and withdraw options — all in one place.',
              },
              {
                icon: Smartphone,
                title: 'Three tabs, everything you need',
                body: 'Home (dashboard + avatar), Wallet (credits & billing), Rewards (Essence earnings). SELF OS is your phone, your AI, and your economy — in your pocket.',
              },
            ].map((feature, i) => (
              <FadeInUp key={feature.title} delay={i * 0.05}>
                <GlowCard className="h-full">
                  <feature.icon className="h-8 w-8 text-sky-400" />
                  <h3 className="mt-4 font-semibold">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{feature.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Role Model AI */}
      <section id="role-model-ai" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeInUp>
              <div>
                <SectionBadge>
                  <Brain className="h-3.5 w-3.5" />
                  Role Model AI
                </SectionBadge>
                <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Create your digital twin — or a team of avatars
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-white/60">
                  Your avatar isn&apos;t just a chatbot. It&apos;s a digital version of you — your look, your
                  voice, your knowledge. Create one to represent you, or build a team of specialized
                  avatars to help run your day-to-day: a scheduler, a researcher, a creative partner.
                </p>
                <div className="mt-8 space-y-4">
                  {[
                    {
                      title: 'Your digital twin',
                      body: 'Train your avatar on your knowledge, style, and preferences. It learns how you think and communicates like you do.',
                    },
                    {
                      title: 'A team that works for you',
                      body: 'Build multiple avatars with different roles. Delegate tasks, automate workflows, and let your AI team handle the routine while you focus on what matters.',
                    },
                    {
                      title: 'Powered by Concierge',
                      body: 'The same platform behind concierge.rolemodel.ai — living avatars with memory, goals, and the ability to take action through tools and integrations.',
                    },
                  ].map((item) => (
                    <GlowCard key={item.title} className="p-5">
                      <h3 className="font-semibold text-white/80">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-white/55">{item.body}</p>
                    </GlowCard>
                  ))}
                </div>
                <div className="mt-6">
                  <GlowButton href="https://concierge.rolemodel.ai" primary>
                    Create your avatar
                    <ArrowRight className="h-4 w-4" />
                  </GlowButton>
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.15}>
              <div className="relative flex items-center justify-center">
                <div className="relative animate-float-glow">
                  <div className="absolute -inset-4 rounded-full bg-sky-400/5 blur-3xl" />
                  <img
                    src="/assets/images/ChatGPT Image Aug 6, 2026, 04_55_01 PM.png"
                    alt="Digital twin and avatar team concept"
                    className="relative w-full max-w-[500px] rounded-3xl border border-white/10 shadow-[0_0_30px_rgba(56,189,248,0.06)]"
                  />
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 5. Resonatia Node */}
      <section id="resonatia" className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Zap className="h-3.5 w-3.5" />
                Resonatia × SELF Phone
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Your device is a Resonatia node
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Resonatia is our global AI API — hundreds of edge-optimized models through one unified
                endpoint. SELF Phone turns your hardware into mesh infrastructure.
              </p>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {NODE_STEPS.map((step, i) => (
              <FadeInUp key={step.title} delay={i * 0.06}>
                <GlowCard className="text-center">
                  <step.icon className="mx-auto h-8 w-8 text-sky-400" />
                  <h3 className="mt-4 font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{step.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp delay={0.2}>
            <div className="mx-auto mt-12 max-w-xl text-center">
              <GlowButton href="https://resonatia.io" primary>
                Learn about Resonatia <ExternalLink className="inline-block h-4 w-4 shrink-0" />
              </GlowButton>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 6. Essence */}
      <section id="essence" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Coins className="h-3.5 w-3.5" />
                Rewards settlement
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Your node earns Essence
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Essence (ESSENCE) is the economic layer of SELF OS — pegged at ~$1 notional and
                intended to be USDC-backed 1:1 by the EssenceTreasury once the vault is funded.
                When your SELF Phone helps process Resonatia API requests as a mesh node, you
                earn Essence rewards.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.05}>
            <div className="mx-auto mt-10 max-w-3xl">
              <div className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-black shadow-[0_0_30px_rgba(56,189,248,0.04)] transition-all duration-500 hover:border-sky-400/25 hover:shadow-[0_0_40px_rgba(56,189,248,0.08)]">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-sky-500/10 via-cyan-500/5 to-sky-500/10 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />
                <video
                  className="relative w-full rounded-2xl"
                  controls
                  playsInline
                  preload="metadata"
                >
                  <source src="/assets/video/Future_of_AI__ESSENCE.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-5xl gap-8 sm:grid-cols-3">
            {[
              {
                icon: Coins,
                title: 'What is Essence?',
                body: 'Essence is a token pegged at ~$1 notional for node rewards, agent payments, and settlement. Resonatia inference spend funds the treasury that backs node rewards. It is prototype-stage and rate-limited — not cash and not a stablecoin in the legal sense.',
              },
              {
                icon: Globe,
                title: 'How you earn',
                body: 'Sync Resonatia models to your SELF Phone and stay online. When API traffic routes through your mesh node, you earn Essence automatically — no claims required.',
              },
              {
                icon: Zap,
                title: 'What you can do with it',
                body: 'Hold Essence for upgrades, convert toward USDC via the treasury, or spend in the Role Model AI ecosystem. It is real value for real contributions.',
              },
            ].map((item, i) => (
              <FadeInUp key={item.title} delay={i * 0.08}>
                <GlowCard className="p-8 text-center">
                  <div className="flex justify-center">
                    <item.icon className="h-12 w-12 text-sky-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp delay={0.15}>
            <div className="mx-auto mt-16 max-w-3xl text-center">
              <SectionBadge>
                <Moon className="h-3.5 w-3.5" />
                Idle compute
              </SectionBadge>
              <h2 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                Earn while you sleep
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Your phone already sits idle most of the day. SELF OS puts that idle time to work —
                running real AI inference for the Resonatia network and earning you Essence.
              </p>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-10 grid max-w-5xl gap-6 sm:grid-cols-3">
            {[
              {
                icon: Download,
                title: '1. Opt in & download a model',
                body: 'Choose a model from the Resonatia Model Library and download it. You control when it runs: set rules for charging, battery level, Wi‑Fi, and a night schedule — your phone, your rules.',
              },
              {
                icon: Cpu,
                title: '2. Idle inference',
                body: 'When your phone is idle (screen off, rules passed), it runs local AI inference using llama.cpp on-device. No cloud, no data leaving your phone — just pure compute for the Resonatia network.',
              },
              {
                icon: Coins,
                title: '3. Earn Essence',
                body: 'Verified work earns Essence at ~$1 notional per Essence. Convert Essence into Talk credits (100 credits = 1 Essence) to fund avatar conversations — live today. On-chain withdraw is the planned future rail.',
              },
            ].map((step, i) => (
              <FadeInUp key={step.title} delay={i * 0.08}>
                <GlowCard className="p-8 text-center h-full">
                  <div className="flex justify-center">
                    <step.icon className="h-12 w-12 text-sky-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{step.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp delay={0.25}>
            <div className="mx-auto mt-16 max-w-2xl rounded-2xl border border-sky-400/15 bg-gradient-to-br from-sky-500/5 via-white/[0.02] to-transparent p-8 text-center">
              <p className="text-sm leading-relaxed text-white/60">
                Essence is pegged at ~$1 notional and is intended to be USDC-backed 1:1 by the
                EssenceTreasury once the vault is funded. It is prototype-stage and rate-limited — not
                cash and not a stablecoin in the legal sense — powered by the same infrastructure
                that runs SELF OS and Role Model AI.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.3}>
            <div className="mx-auto mt-12 max-w-xl text-center">
              <GlowButton href="https://essence.selflabs.xyz" primary>
                Learn more about Essence <ExternalLink className="inline-block h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </GlowButton>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 7. Security */}
      <section id="security" className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Shield className="h-3.5 w-3.5" />
                Protection
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                How We Protect Your Signal
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Your signal is your sovereignty. We protect it at every level.
              </p>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-5xl gap-6 sm:grid-cols-3">
            {SECURITY_FEATURES.map((item, i) => (
              <FadeInUp key={item.title} delay={i * 0.08}>
                <GlowCard>
                  <item.icon className="h-8 w-8 text-sky-400" />
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.body}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Designed for Your Digital Life */}
      <section className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <FadeInUp>
              <div>
                <SectionBadge>
                  <Sparkles className="h-3.5 w-3.5" />
                  Design
                </SectionBadge>
                <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                  Designed for Your Digital Life
                </h2>
                <div className="mt-8 space-y-6">
                  {[
                    {
                      title: 'Seamless Integration',
                      body: 'Experience fluid interaction between your AI agents and daily tasks, all through an intuitive interface.',
                    },
                    {
                      title: 'Satellite Connectivity',
                      body: 'Stay connected anywhere with direct satellite communication, no traditional carrier required.',
                    },
                    {
                      title: 'Privacy First',
                      body: 'Built-in Aura Shield ensures your data remains protected and under your control at all times.',
                    },
                    {
                      title: 'Earn As You Serve',
                      body: 'Run Resonatia models on your node and earn Essence every time the API routes inference through your device.',
                    },
                  ].map((item) => (
                    <GlowCard key={item.title} className="p-5">
                      <h3 className="font-semibold text-white/80">{item.title}</h3>
                      <p className="mt-1 text-sm text-white/55">{item.body}</p>
                    </GlowCard>
                  ))}
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.15}>
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-full bg-sky-400/5 blur-2xl" />
                  <motion.img
                    src="/assets/images/Untitled design (2).svg"
                    alt="SELF Phone Features"
                    className="relative w-full max-w-[500px] rounded-3xl border border-white/10"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
              </div>
              <p className="mt-4 text-center text-sm text-white/40 italic">
                *Actual device appearance may vary from images shown
              </p>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* 9. Specs */}
      <section id="specs" className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Smartphone className="h-3.5 w-3.5" />
                Hardware
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                SELF Phone Specs
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Built for Power. Designed for Privacy. Connected to Signal.
              </p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <div className="mx-auto mt-12 flex max-w-4xl items-center justify-center">
              <motion.img
                src="/assets/images/SELF PHONE2.svg"
                alt="SELF Phone Specifications"
                className="w-full max-w-[700px] rounded-3xl border border-white/10 shadow-[0_0_24px_rgba(56,189,248,0.04)]"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </div>
            <p className="mt-4 text-center text-sm text-white/40 italic">
              *Actual device appearance may vary from images shown
            </p>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SPECS.map((spec, i) => (
              <FadeInUp key={spec.label} delay={i * 0.03}>
                <GlowCard className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    {spec.label}
                  </p>
                  <p className="mt-2 text-sm text-white/75">{spec.value}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Unboxing */}
      <section id="unboxing" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Package className="h-3.5 w-3.5" />
                Unboxing
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                What&apos;s Inside Your SELF Box
              </h2>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.1}>
            <div className="mx-auto mt-12 flex max-w-4xl items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-4 rounded-full bg-sky-400/5 blur-2xl" />
                <motion.img
                  src="/assets/images/SELF.svg"
                  alt="SELF Box Contents"
                  className="relative w-full max-w-[700px] rounded-3xl border border-white/10"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                />
              </div>
            </div>
            <p className="mt-4 text-center text-sm text-white/40 italic">
              *Actual device appearance may vary from images shown
            </p>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHATS_IN_BOX.map((item, i) => (
              <FadeInUp key={item.title} delay={i * 0.05}>
                <GlowCard className="p-5">
                  <h3 className="font-semibold text-white/80">{item.title}</h3>
                  <p className="mt-1 text-sm text-white/55">{item.detail}</p>
                </GlowCard>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 11. Shipping in Batches */}
      <section id="batches" className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Truck className="h-3.5 w-3.5" />
                Shipping
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Shipping in batches
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                SELF Phone ships in five limited batches — 10 phones per batch. Your batch is assigned
                at checkout based on real-time availability.
              </p>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-16 max-w-2xl space-y-3">
            {inventory.batches.map((batch) => {
              const claimedPct =
                batch.size > 0 ? Math.min(100, (batch.sold / batch.size) * 100) : 0;
              const isOpen = batch.status === 'open';

              return (
                <FadeInUp key={batch.number} delay={batch.number * 0.04}>
                  <div
                    className={`rounded-2xl border p-5 transition-all duration-300 ${
                      isOpen
                        ? 'border-sky-400/25 bg-gradient-to-r from-sky-500/5 via-white/[0.03] to-transparent'
                        : 'border-white/10 bg-white/[0.02]'
                    } ${isOpen ? 'hover:border-sky-400/40 hover:shadow-[0_0_16px_rgba(56,189,248,0.06)]' : ''}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${
                            isOpen
                              ? 'bg-sky-500/15 text-sky-400'
                              : batch.status === 'complete'
                                ? 'bg-white/10 text-white/40'
                                : 'bg-white/5 text-white/50'
                          }`}
                        >
                          {batch.number}
                        </span>
                        <div>
                          <p className="font-semibold text-white">
                            Batch {batch.number}
                            {isOpen && (
                              <span className="ml-2 text-xs font-medium text-sky-400">· current</span>
                            )}
                          </p>
                          <p className="mt-0.5 text-sm text-white/55">Ships {batch.shipLabel}</p>
                        </div>
                      </div>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                          batch.status === 'open'
                            ? 'border-sky-400/20 bg-sky-500/10 text-sky-400'
                            : batch.status === 'complete'
                              ? 'border-white/10 text-white/40'
                              : 'border-white/10 text-white/50'
                        }`}
                      >
                        {batch.status === 'complete'
                          ? 'Full'
                          : batch.status === 'open'
                            ? 'Open now'
                            : 'Upcoming'}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-white/50">
                      <span>
                        {batch.status === 'complete'
                          ? `${batch.size} phones claimed`
                          : batch.status === 'open'
                            ? `${batch.remaining} of ${batch.size} spots left`
                            : `${batch.size} phones in batch`}
                      </span>
                      {batch.status !== 'upcoming' && (
                        <span>{batch.sold} claimed</span>
                      )}
                    </div>

                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out ${
                          batch.status === 'complete'
                            ? 'bg-white/30'
                            : 'bg-gradient-to-r from-sky-400 to-cyan-400'
                        }`}
                        style={{ width: `${claimedPct}%` }}
                      />
                    </div>
                  </div>
                </FadeInUp>
              );
            })}
          </div>

          <FadeInUp delay={0.3}>
            <div className="mx-auto mt-10 grid max-w-4xl gap-6 sm:grid-cols-2">
              <GlowCard>
                <h3 className="mb-3 font-semibold text-white/80">How batches work</h3>
                <ul className="space-y-2 text-sm text-white/55">
                  <li>• 50 phones total across 5 batches (10 per batch)</li>
                  <li>• When a batch fills, the next order moves to the following ship date</li>
                  <li>• Your confirmation email includes your batch number and ship window</li>
                  <li>• Carrier: secured express delivery with tracking</li>
                </ul>
              </GlowCard>
              <GlowCard>
                <h3 className="mb-3 font-semibold text-white/80">Order protection</h3>
                <p className="text-sm text-white/55">
                  Your order is protected by the SELF Labs Secure Order Guarantee. Batch assignment
                  is locked in at purchase — you always know which wave you&apos;re in before your
                  phone ships.
                </p>
              </GlowCard>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* 12. Built for Real-World Use */}
      <section id="trust" className="border-t border-white/[0.06] bg-white/[0.01] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <SectionBadge>
                <Shield className="h-3.5 w-3.5" />
                Production-ready foundations
              </SectionBadge>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Built for real-world use
              </h2>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2">
            <FadeInUp delay={0.05}>
              <GlowCard>
                <div className="flex gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                  <div>
                    <h3 className="font-semibold">Hardened release</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/55">
                      Signed with a unique release key. Minified build, encrypted session storage,
                      backups disabled, WebView debugging off, prototype-only permissions removed.
                    </p>
                  </div>
                </div>
              </GlowCard>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <GlowCard>
                <div className="flex gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
                  <div>
                    <h3 className="font-semibold">Beta realities</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/55">
                      Prototype software — features may change or be rate-limited. Idle compute uses
                      some battery and CPU. Sideloaded APKs can be copied — install only from Role
                      Model / Self Labs (checksums published).
                    </p>
                  </div>
                </div>
              </GlowCard>
            </FadeInUp>
            <FadeInUp delay={0.15}>
              <GlowCard>
                <div className="flex gap-3">
                  <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                  <div>
                    <h3 className="font-semibold">Requirements</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/55">
                      Android 9+ · Role Model AI account · Published Living Bio for Talk
                      (concierge.rolemodel.ai) · ~500 MB free storage
                    </p>
                  </div>
                </div>
              </GlowCard>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <GlowCard>
                <div className="flex gap-3">
                  <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                  <div>
                    <h3 className="font-semibold">Support</h3>
                    <p className="mt-1 text-sm leading-relaxed text-white/55">
                      Questions? Contact <span className="text-white/80">support@rolemodel.ai</span>.
                      For the latest builds and checksums, visit selflabs.xyz.
                    </p>
                  </div>
                </div>
              </GlowCard>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Frequently Asked Questions
              </h2>
            </div>
          </FadeInUp>

          <div className="mx-auto mt-16 grid max-w-4xl gap-4">
            {[
              {
                q: 'Is SELF OS a replacement for Android?',
                a: 'No — SELF OS is a sideloaded app that runs on top of Android 9+. It adds your Role Model AI avatar, side-button assistant, Concierge dashboard, and idle compute earning to your existing phone. No root, no bootloader unlock, no OS replacement.',
              },
              {
                q: 'What is the Resonatia node on SELF Phone?',
                a: 'SELF Phone is built to run as a Resonatia mesh node. You download edge-optimized models from Resonatia, sync them to the network, and earn Essence when your device helps process API requests routed through the mesh.',
              },
              {
                q: 'How do I earn rewards?',
                a: 'Sync Resonatia models to your device and keep your node online. When developers call the Resonatia API, requests can be processed by mesh nodes like yours — and you earn Essence for every contribution. Essence is pegged at ~$1 notional, prototype-stage and rate-limited.',
              },
              {
                q: 'Is my data safe?',
                a: 'Yes. Aura Shield locks your data automatically. Only you control whether anything is ever shared. SELF OS is signed with a unique release key, uses encrypted session storage, and has WebView debugging disabled.',
              },
              {
                q: 'Can I use my own SIM card?',
                a: 'Yes. The SELF Phone supports two Micro SIM cards for optional carrier connectivity — but global satellite messaging works without any SIM.',
              },
              {
                q: 'Does idle compute drain my battery?',
                a: 'You control everything. Set rules for when compute runs: only while charging, only above a battery threshold, only on Wi‑Fi, only during certain hours. When no rules are met, the phone stays completely idle. Safety limits on CPU and battery are always enforced.',
              },
              {
                q: 'Can I use my existing Concierge account?',
                a: 'Yes — same account system as Concierge (concierge.rolemodel.ai). Sign in with your existing Role Model AI credentials and your avatar, credits, and history are all there.',
              },
              {
                q: 'Which phones are supported?',
                a: 'Any Android device running Android 9 or later with ~500 MB free storage. SELF OS is a sideloaded APK — no root required, no bootloader unlock. Install only from Role Model / Self Labs and verify checksums from selflabs.xyz.',
              },
              {
                q: 'Is Essence real money?',
                a: 'Essence is pegged at ~$1 notional and is intended to be USDC-backed 1:1 by the EssenceTreasury once the vault is funded. Today it is prototype-stage and rate-limited — not cash and not a stablecoin in the legal sense. You can convert Essence into Talk credits (100 credits per Essence) to fund avatar conversations right now.',
              },
              {
                q: 'Why only 50 phones?',
                a: 'The first SELF Phone wave is limited to 50 units across 5 batches. When they are gone, checkout closes automatically.',
              },
              {
                q: 'When will I receive my phone?',
                a: 'Your ship date depends on your batch. Currently filling Batch 1 (September 1, 2026). When Batch 1 sells out, new orders move to Batch 2 (September 30, 2026). Your batch is confirmed at checkout.',
              },
            ].map((faq, i) => (
              <FadeInUp key={faq.q} delay={i * 0.03}>
                <details className="group rounded-2xl border border-white/10 bg-white/[0.03] transition-all hover:border-sky-400/20">
                  <summary className="flex cursor-pointer items-center justify-between px-6 py-5 text-base font-medium text-white/80">
                    {faq.q}
                    <ChevronRight className="h-4 w-4 shrink-0 text-white/40 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="border-t border-white/[0.06] px-6 py-5">
                    <p className="text-sm leading-relaxed text-white/55">{faq.a}</p>
                  </div>
                </details>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* 14. Final CTA */}
      <section className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <FadeInUp>
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Ready to join the mesh?
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-white/60">
                Get the SELF Phone or sideload SELF OS on your Android device. Either way, you become a
                node on the Resonatia network.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <GlowButton onClick={openCheckout} disabled={checkoutLoading} hero>
                  {checkoutLoading ? 'Opening checkout...' : 'Buy SELF Phone $999'}
                  <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </GlowButton>
                <GlowButton href="https://selflabs.xyz/download">
                  <Download className="h-5 w-5" />
                  Download SELF OS beta
                </GlowButton>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
}